import type {
  AutoRuleExecutionListItem,
  AutoRuleExecutionQuery,
  AutoRuleExecutionResponseItem,
  AutoRulePageResponse,
  AutoRulePagingResult,
} from './types'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'

import {
  getDateTimeRange,
  omitBlankString,
  toAutoRuleMediaPlatform,
  toBackendInt64,
} from './convert'

type AutoRuleExecutionPageRequest = PagingRequest<{
  condition?: AutoRuleExecutionQuery
}>

function getExecutionRequest(params: AutoRuleExecutionPageRequest) {
  const executedAt = getDateTimeRange(params.condition?.executionTimeRange)

  return {
    actionType: params.condition?.actionType,
    adAccountId: omitBlankString(params.condition?.adAccountId),
    adId: omitBlankString(params.condition?.adId),
    adSetId: omitBlankString(params.condition?.adSetId),
    campaignId: omitBlankString(params.condition?.campaignId),
    customerKeyword: omitBlankString(params.condition?.customerKeyword),
    executedAtEnd: executedAt.end,
    executedAtStart: executedAt.start,
    mediaPlatform: toAutoRuleMediaPlatform(params.condition?.mediaPlatform),
    merchantId: toBackendInt64(params.condition?.merchantId),
    monitoringTarget: params.condition?.monitoringTarget,
    pageIndex: params.pageIndex,
    pageSize: params.pageSize,
    ruleId: toBackendInt64(params.condition?.ruleId),
    ruleKeyword: omitBlankString(params.condition?.ruleKeyword),
    status: params.condition?.status,
  }
}

function mapExecutionItem(
  item: AutoRuleExecutionResponseItem,
): AutoRuleExecutionListItem {
  return {
    actionResult: item.actionResult,
    actionType: item.actionType,
    adAccountId: item.adAccountId,
    adAccountName: item.adAccountName,
    adId: item.adId,
    adName: item.adName,
    adSetId: item.adSetId,
    adSetName: item.adSetName,
    campaignId: item.campaignId,
    campaignName: item.campaignName,
    customerId: item.customerId,
    customerName: item.customerName,
    errorMessage: item.errorMessage,
    executionId: item.executionId,
    finishedAt: item.finishedAt,
    id: item.id,
    mediaPlatform: item.mediaPlatform,
    merchantId: item.merchantId,
    metricSnapshot: item.metricSnapshot,
    ruleId: item.ruleId,
    ruleName: item.ruleName,
    ruleSnapshot: item.ruleSnapshot,
    ruleVersion: item.ruleVersion,
    scheduledAt: item.scheduledAt,
    skipReason: item.skipReason,
    startedAt: item.startedAt,
    status: item.status,
    targetType: item.targetType,
  }
}

export const autoRuleExecutionApi = defineApi('operation', ({ prefix }) => {
  /**
   * 分页查询规则执行日志。
   * POST /gobestads/operation/adsAutoRule/executionLog/page
   */
  function queryExecutions(
    params: AutoRuleExecutionPageRequest,
  ): Promise<AutoRulePagingResult<AutoRuleExecutionListItem>> {
    return requestClient
      .post<
        AutoRulePageResponse<AutoRuleExecutionResponseItem>
      >(prefix('adsAutoRule/executionLog/page'), getExecutionRequest(params))
      .then((res) => ({
        items: (res?.list ?? []).map((item) => mapExecutionItem(item)),
        total: res?.total,
      }))
  }

  return {
    queryExecutions,
  }
})
